/**
 * @license Copyright (c) 2014-2020, CKSource - Frederico Knabben. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */
import ClassicEditor from "@ckeditor/ckeditor5-editor-classic/src/classiceditor.js";
import Alignment from "@ckeditor/ckeditor5-alignment/src/alignment.js";
import Autoformat from "@ckeditor/ckeditor5-autoformat/src/autoformat.js";
import BlockQuote from "@ckeditor/ckeditor5-block-quote/src/blockquote.js";
import Bold from "@ckeditor/ckeditor5-basic-styles/src/bold.js";
import Code from "@ckeditor/ckeditor5-basic-styles/src/code.js";
import CodeBlock from "@ckeditor/ckeditor5-code-block/src/codeblock.js";
import Essentials from "@ckeditor/ckeditor5-essentials/src/essentials.js";
import FontBackgroundColor from "@ckeditor/ckeditor5-font/src/fontbackgroundcolor.js";
import FontColor from "@ckeditor/ckeditor5-font/src/fontcolor.js";
import FontFamily from "@ckeditor/ckeditor5-font/src/fontfamily.js";
import FontSize from "@ckeditor/ckeditor5-font/src/fontsize.js";
import Heading from "@ckeditor/ckeditor5-heading/src/heading.js";
import Highlight from "@ckeditor/ckeditor5-highlight/src/highlight.js";
import HorizontalLine from "@ckeditor/ckeditor5-horizontal-line/src/horizontalline.js";
import Image from "@ckeditor/ckeditor5-image/src/image.js";
import ImageCaption from "@ckeditor/ckeditor5-image/src/imagecaption.js";
import ImageResize from "@ckeditor/ckeditor5-image/src/imageresize.js";
import ImageStyle from "@ckeditor/ckeditor5-image/src/imagestyle.js";
import ImageToolbar from "@ckeditor/ckeditor5-image/src/imagetoolbar.js";
import ImageUpload from "@ckeditor/ckeditor5-image/src/imageupload.js";
import Indent from "@ckeditor/ckeditor5-indent/src/indent.js";
import IndentBlock from "@ckeditor/ckeditor5-indent/src/indentblock.js";
import Italic from "@ckeditor/ckeditor5-basic-styles/src/italic.js";
import Link from "@ckeditor/ckeditor5-link/src/link.js";
import LinkImage from "@ckeditor/ckeditor5-link/src/linkimage.js";
import List from "@ckeditor/ckeditor5-list/src/list.js";
import ListStyle from "@ckeditor/ckeditor5-list/src/liststyle.js";
import MediaEmbed from "@ckeditor/ckeditor5-media-embed/src/mediaembed.js";
import Paragraph from "@ckeditor/ckeditor5-paragraph/src/paragraph.js";
import PasteFromOffice from "@ckeditor/ckeditor5-paste-from-office/src/pastefromoffice";
import SpecialCharacters from "@ckeditor/ckeditor5-special-characters/src/specialcharacters.js";
import Table from "@ckeditor/ckeditor5-table/src/table.js";
import TableCellProperties from "@ckeditor/ckeditor5-table/src/tablecellproperties";
import TableProperties from "@ckeditor/ckeditor5-table/src/tableproperties";
import TableToolbar from "@ckeditor/ckeditor5-table/src/tabletoolbar.js";
import TextTransformation from "@ckeditor/ckeditor5-typing/src/texttransformation.js";
import TodoList from "@ckeditor/ckeditor5-list/src/todolist";
import Underline from "@ckeditor/ckeditor5-basic-styles/src/underline.js";

import Plugin from "@ckeditor/ckeditor5-core/src/plugin";
import {
  toWidget,
  toWidgetEditable,
} from "@ckeditor/ckeditor5-widget/src/utils";
import Widget from "@ckeditor/ckeditor5-widget/src/widget";
import ButtonView from "@ckeditor/ckeditor5-ui/src/button/buttonview";

import attachFileIcon from "../../../images/icons/attach-file.svg";

class FileUploader extends Plugin {
  static get requires() {
    return [Widget];
  }

  init() {
    this.editor.model.schema.register("fileContainer", {
      isObject: true,
      allowWhere: "$block",
    });
    this.editor.model.schema.register("fileIcon", {
      allowIn: "fileContainer",
      allowContentOf: "$block",
    });
    this.editor.model.schema.register("fileName", {
      allowIn: "fileContainer",
      allowContentOf: "$block",
    });
    this.editor.model.schema.register("downloadBtn", {
      allowIn: "fileContainer",
      allowContentOf: "$block",
      allowAttributes: ["href", "download"],
    });
    this.editor.conversion.for("upcast").elementToElement({
      model: "fileContainer",
      view: {
        name: "div",
        classes: "file-container",
      },
    });
    this.editor.conversion.for("dataDowncast").elementToElement({
      model: "fileContainer",
      view: {
        name: "div",
        classes: "file-container",
      },
    });
    this.editor.conversion.for("editingDowncast").elementToElement({
      model: "fileContainer",
      view: (modelElement, { writer: viewWriter }) => {
        const div = viewWriter.createContainerElement("div", {
          class: "file-container",
        });

        return toWidget(div, viewWriter);
      },
    });
    this.editor.conversion.for("upcast").elementToElement({
      model: "fileIcon",
      view: {
        name: "div",
        classes: "file-icon",
      },
    });
    this.editor.conversion.for("dataDowncast").elementToElement({
      model: "fileIcon",
      view: {
        name: "div",
        classes: "file-icon",
      },
    });
    this.editor.conversion.for("editingDowncast").elementToElement({
      model: "fileIcon",
      view: {
        name: "div",
        classes: "file-icon",
      },
    });
    this.editor.conversion.for("upcast").elementToElement({
      model: "fileName",
      view: {
        name: "div",
        classes: "file-name",
      },
    });
    this.editor.conversion.for("dataDowncast").elementToElement({
      model: "fileName",
      view: {
        name: "div",
        classes: "file-name",
      },
    });
    this.editor.conversion.for("editingDowncast").elementToElement({
      model: "fileName",
      view: {
        name: "div",
        classes: "file-name",
      },
    });
    this.editor.conversion.for("upcast").elementToElement({
      model: "downloadBtn",
      view: {
        name: "a",
        classes: "download-icon",
      },
    });
    this.editor.conversion.for("dataDowncast").elementToElement({
      model: "downloadBtn",
      view: {
        name: "a",
        classes: "download-icon",
      },
    });
    this.editor.conversion.for("editingDowncast").elementToElement({
      model: "downloadBtn",
      view: {
        name: "a",
        classes: "download-icon",
      },
    });
    this.editor.conversion.attributeToAttribute({
      model: {
        key: "href",
      },
      view: {
        key: "href",
      },
    });
    this.editor.conversion.attributeToAttribute({
      model: {
        key: "download",
      },
      view: {
        key: "download",
      },
    });

    const editor = this.editor;
    editor.model.schema.extend("paragraph", {
      allowAttributes: "fileContainer",
    });

    editor.ui.componentFactory.add("fileUpload", () => {
      // The button will be an instance of ButtonView.
      const button = new ButtonView();

      button.set({
        label: "파일첨부",
        icon: attachFileIcon,
        tooltip: true,
      });

      // Execute a callback function when the button is clicked.
      button.on("execute", () => {
        const fileInput = document.createElement("input");
        fileInput.setAttribute("type", "file");
        fileInput.setAttribute("class", "d-none");
        fileInput.addEventListener("change", function () {
          const target = this.files[0];

          const xhr = new XMLHttpRequest();
          xhr.open("POST", "https://api.fruiting.co.kr/api/v1/upload", true);
          xhr.responseType = "json";

          xhr.addEventListener("error", () =>
            console.error("error", `Couldn't upload file: ${target.name}`)
          );
          xhr.addEventListener("abort", () =>
            console.error("abort", `Couldn't upload file: ${target.name}`)
          );
          xhr.addEventListener("load", () => {
            const response = xhr.response;

            if (!response || response.error) {
              return response && response.error
                ? console.error("load", response.error.message)
                : console.error("load", `Couldn't upload file: ${target.name}`);
            }

            editor.model.change((writer) => {
              const fileContainer = writer.createElement("fileContainer");
              const fileIcon = writer.createElement("fileIcon");
              const fileName = writer.createElement("fileName");
              const downloadBtn = writer.createElement("downloadBtn", {
                href: response.uploaded_url,
                download: target.name,
              });

              writer.append(fileIcon, fileContainer);
              writer.append(fileName, fileContainer);
              writer.append(downloadBtn, fileContainer);

              writer.appendText(target.name, fileName);

              editor.model.insertContent(fileContainer);
            });
          });

          const data = new FormData();
          data.append("upload", target);
          xhr.send(data);
        });
        fileInput.click();
      });

      return button;
    });
  }
}

class Editor extends ClassicEditor {}

// Plugins to include in the build.
Editor.builtinPlugins = [
  Alignment,
  Autoformat,
  BlockQuote,
  Bold,
  Code,
  CodeBlock,
  Essentials,
  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize,
  Heading,
  Highlight,
  HorizontalLine,
  Image,
  ImageCaption,
  ImageResize,
  ImageStyle,
  ImageToolbar,
  ImageUpload,
  Indent,
  IndentBlock,
  Italic,
  Link,
  LinkImage,
  List,
  ListStyle,
  MediaEmbed,
  Paragraph,
  PasteFromOffice,
  SpecialCharacters,
  Table,
  TableCellProperties,
  TableProperties,
  TableToolbar,
  TextTransformation,
  TodoList,
  Underline,
  FileUploader,
];

export default Editor;
